import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import BackgroundCheckCallout from '@rover/components/src/callouts/BackgroundCheckCallout';
import SearchResultCard from '@rover/components/src/cards/SearchResultCard';
import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Flex, Heading } from '@rover/kibble/core';
import { BannerSeverity } from '@rover/kibble/patterns/Banner/BannerSeverity.constants';
import Toast from '@rover/kibble/patterns/Toast';
import useTheme from '@rover/kibble/themes/useTheme';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import { SearchCardClickedEvent } from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import { shouldShowMaximalSearchCard } from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import { getBookingFeeLearnMoreUrl } from '@rover/react-lib/src/utils/bookingFeeUtils';
import type { CountryConfigurationUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrls';
import type { OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResult';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import useIsBHUserShim from '@rover/shared/js/hooks/useIsBHUserShim';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { Callout } from '@rover/types/src/Callout';
import { CalloutSlugs } from '@rover/types/src/Callout';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import {
  getIsPriceTransparency,
  getIsPriceTransparencyCondensed,
} from '@rover/utilities/priceTransparency';

import { usePetInfoCallout } from '../../utilities/usePetInfoCallout';
import { useSimplifiedFilters } from '../../utilities/useSimplifiedFilters';
import { BookingFeeLinkClickSearchEvent, BookingFeeLinkClickSeoEvent } from '../analyticsEvents';
import FeeExplanationBottomSheet from '../BottomSheets/FeeExplanationBottomSheet';
import { FeeExplanationModal } from '../Modals/FeeExplanationModal';
import PtCondensedBanner from '../PtCondensedBanner/PtCondensedBanner';
import SearchSectionHeader, {
  HeaderScenariosEnum,
  searchHeaderBuilder,
} from '../SearchSectionHeader';
import SelectedProvider from '../SelectedProvider';

import AlternateSearchResults from './AlternateSearchResults';
import { EmptyResults, SearchError } from './Errors';
import MaximalSearchCardsGrid, { type MaximalGridItem } from './MaximalSearchCardsGrid';
import { getCallout } from './utils';

export type Props = {
  alternateSearchResults: SearchResult[];
  alternateServiceName: string;
  alternateServiceDescription: string;
  bookingFeePercentage?: string;
  countryConfigUrls?: CountryConfigurationUrls;
  currentFilters: SearchFilters;
  didFiltersChangeSincePageLoad: boolean;
  hoveringResultIndex?: number | null;
  isAuthenticated?: boolean | null;
  isMapExpanded: boolean;
  locale: Locale;
  memberProfileLinkParams: Record<string, string | undefined>;
  priceTransparencyLevel?: PriceTransparencyLevel;
  searchErrorMessage: string;
  searchResults: SearchResult[];
  selectedProviderResult?: SearchResult;
  searchQueryDates: Record<'startDate' | 'endDate', Date | undefined>;
  searchQueryDistinctDates: { dates?: string };
  searchResponseNumberOfResults: number;
  isSeoPage: boolean;
  onFavoriteToggled: (personOpk: string) => void;
  updateFiltersAndFireSearchOnChange?: (filters: Partial<SearchFilters>) => void;
  onResultCardMouseEnter?: (index: number) => void;
  onResultCardMouseLeave?: () => void;
  searchCardRefs?: (instance: HTMLDivElement, index: number) => void;
  preferCatReviews: boolean;
  onClick?: (entityOpk: string) => void;
  searchCallouts?: Callout[];
  isShowingPriceInCurrency?: boolean;
  cardTreatment: SearchCardTreatment;
};

const SearchResults: React.FC<Props> = ({
  alternateSearchResults,
  alternateServiceName,
  alternateServiceDescription,
  bookingFeePercentage,
  countryConfigUrls,
  currentFilters,
  didFiltersChangeSincePageLoad,
  memberProfileLinkParams,
  priceTransparencyLevel,
  searchErrorMessage,
  searchResults,
  selectedProviderResult,
  searchQueryDates,
  searchQueryDistinctDates,
  isAuthenticated,
  isMapExpanded, // This controls the size of SearchResultsCard for viewports < 992px
  locale,
  onFavoriteToggled,
  updateFiltersAndFireSearchOnChange,
  searchResponseNumberOfResults,
  onResultCardMouseEnter,
  onResultCardMouseLeave,
  hoveringResultIndex,
  searchCardRefs,
  isSeoPage,
  preferCatReviews,
  onClick = () => {},
  searchCallouts,
  isShowingPriceInCurrency = true,
  cardTreatment,
}) => {
  const theme = useTheme();
  const { setScreenReaderAnnouncement } = useA11yContext();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const source = isMobileBrowser() ? 'mobile_web' : 'web';

  const searchErrorOccurred = searchErrorMessage !== '';
  const noSearchResults = !searchResults || searchResults.length === 0;
  const isPriceTransparency = getIsPriceTransparency(priceTransparencyLevel);
  const showMaximalSearchCard = shouldShowMaximalSearchCard(cardTreatment, isSeoPage);
  const { serviceType, dogCount, catCount, puppyCount, spacesRequired, pet, frequency } =
    currentFilters;

  const [isFeeExplanationModalOpen, setIsFeeExplanationModalOpen] = useState(false);

  const isBrightHorizonsUser = useIsBHUserShim();

  const { isActive: isInSimplifiedMobileFilters } = useSimplifiedFilters();

  const { renderCalloutAtIndex, toastText, onToastClose } = usePetInfoCallout({
    callouts: searchCallouts || [],
    dogSet: { petIds: pet, dogCount: dogCount || 0, puppyCount: puppyCount || 0 },
    updateFiltersAndFireSearchOnChange,
  });

  useEffect(() => {
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    setPortalTarget(document.body);
  }, []);

  // Shortlist filters synchronization
  const { setSearchFilters: setShortlistSearchFilters } = useShortlist();
  useEffect(() => {
    setShortlistSearchFilters(currentFilters);
  }, [currentFilters, setShortlistSearchFilters]);

  // Enhanced memberProfileLinkParams that includes pet counts for BH users
  const enhancedMemberProfileLinkParams = useMemo(() => {
    if (isBrightHorizonsUser) {
      return {
        ...memberProfileLinkParams,
        dogCount: dogCount?.toString(),
        catCount: catCount?.toString(),
        puppyCount: puppyCount?.toString(),
      };
    }
    return memberProfileLinkParams;
  }, [memberProfileLinkParams, isBrightHorizonsUser, dogCount, catCount, puppyCount]);

  const alertLinkEvent = isSeoPage
    ? new BookingFeeLinkClickSeoEvent()
    : new BookingFeeLinkClickSearchEvent();

  /**
   * `onClick` is wrapped so we can build in data without having to prop-drill
   * into `SearchResultCard`s.
   */
  const wrappedOnClick = (entityOpk: string, rank: number | null): void => {
    if (!isPriceTransparency) {
      onClick(entityOpk);
    }

    // Emit profile viewed metric with rank and cardTreatment
    const tags = {
      cardTreatment,
      source,
      rank: rank?.toString() ?? 'none',
    };
    emitAnalyticsEvent(new SearchCardClickedEvent(tags));
  };

  const openFeeExplanationModal = (): void => {
    setIsFeeExplanationModalOpen(true);
  };

  if (searchErrorOccurred) {
    return <SearchError />;
  }

  if (didFiltersChangeSincePageLoad) {
    setScreenReaderAnnouncement(
      t`Search results have changed due to your input.  There are now ${searchResponseNumberOfResults} results.`
    );
  }

  const emptyResultsCallout = getCallout(searchCallouts, CalloutSlugs.emptyResults);

  const emptyResultsComponent = <EmptyResults emptyResultCallout={emptyResultsCallout} />;

  const backgroundCheckCallout = getCallout(searchCallouts, CalloutSlugs.backgroundCheck);
  const isSearchInEurope = backgroundCheckCallout?.data?.badgeSlug === 'id-verified';

  const headers = searchHeaderBuilder({
    dates: searchQueryDates,
    bookingFeePercentage: bookingFeePercentage || '',
    frequency,
    hasResults: !noSearchResults,
    isAuthenticated: !!isAuthenticated,
    locale,
    petData: { dogCount, catCount, puppyCount, petCount: pet.length },
    priceTransparencyLevel: priceTransparencyLevel || PriceTransparencyLevels.none,
    selectedProvider: selectedProviderResult as unknown as OptimizedSearchResult,
    serviceType,
    spacesRequired,
    isSeoPage,
  });

  const selectedProviderHeader = headers[HeaderScenariosEnum.SELECTED_SITTER];
  const mainResultsHeader = headers[HeaderScenariosEnum.SEARCH_RESULTS];

  const shouldDiplayPtMessage =
    bookingFeePercentage &&
    priceTransparencyLevel &&
    getIsPriceTransparencyCondensed(priceTransparencyLevel);

  const shouldHideBanner = isInSimplifiedMobileFilters && !isPriceTransparency;

  const renderCard = (index: number, searchResult: SearchResult): JSX.Element => (
    <SearchResultCard
      displayExpanded={!isMapExpanded}
      index={index}
      isActive={index === hoveringResultIndex}
      isAuthenticated={isAuthenticated}
      key={searchResult.personOpk}
      locale={locale}
      memberProfileLinkParams={enhancedMemberProfileLinkParams}
      onBlur={onResultCardMouseLeave}
      onFavoriteToggled={onFavoriteToggled}
      onFocus={onResultCardMouseEnter}
      onMouseEnter={onResultCardMouseEnter}
      onMouseLeave={onResultCardMouseLeave}
      searchResult={searchResult}
      // @ts-expect-error
      ref={searchCardRefs}
      isSeoPage={isSeoPage}
      preferCatReviews={preferCatReviews}
      onClick={wrappedOnClick}
      priceTransparencyLevel={priceTransparencyLevel}
      itemProp="provider"
      showFromText={!searchQueryDates.startDate || !searchQueryDates.endDate}
      isShowingPriceInCurrency={isShowingPriceInCurrency}
      isBHUser={isBrightHorizonsUser}
      cardTreatment={cardTreatment}
    />
  );

  const mainSearchResults = (): JSX.Element | JSX.Element[] => {
    const items: MaximalGridItem[] = [];

    searchResults.forEach((searchResult: SearchResult, index: number) => {
      const petInfoCallout = renderCalloutAtIndex(index);
      if (petInfoCallout) {
        items.push({
          key: `pet-info-callout-${index}`,
          isFullWidth: true,
          node: (
            <Flex
              display="block"
              sx={{
                gridColumn: showMaximalSearchCard ? '1 / -1' : 'unset',
              }}
            >
              {petInfoCallout}
            </Flex>
          ),
        });
      }

      if (backgroundCheckCallout && index === backgroundCheckCallout.position) {
        items.push({
          key: 'background-check-callout',
          isFullWidth: true,
          node: (
            <Box
              borderBottom={
                showMaximalSearchCard ? undefined : `1px solid ${theme.colors.border.primary}`
              }
              sx={{
                gridColumn: showMaximalSearchCard ? '1 / -1' : 'unset',
              }}
            >
              <BackgroundCheckCallout isInEurope={isSearchInEurope} isInline />
            </Box>
          ),
        });
      }

      items.push({
        key: searchResult.personOpk,
        isFullWidth: false,
        node: renderCard(index, searchResult),
      });
    });

    if (showMaximalSearchCard) {
      return <MaximalSearchCardsGrid items={items} />;
    }

    return items.map((item) => <React.Fragment key={item.key}>{item.node}</React.Fragment>);
  };

  return (
    <>
      {toastText &&
        portalTarget &&
        createPortal(
          <Toast
            severity={BannerSeverity.SUCCESS}
            onClose={onToastClose}
            containerProps={{
              position: 'fixed',
              top: '5x',
              bottom: 'auto',
              left: '0x',
              right: '0x',
              zIndex: ZIndex.TOOLTIP.toString(),
              // @ts-expect-error margin shorthand not in Box types
              margin: '0 auto',
              sx: {
                transform: 'translateY(0)',
              },
            }}
          >
            {toastText}
          </Toast>,
          portalTarget
        )}
      <Heading webProps={{ as: 'h2', a11yHidden: true }}>
        <Trans>Search Results</Trans>
      </Heading>
      {!noSearchResults && getIsPriceTransparencyCondensed(priceTransparencyLevel) && (
        <Box position="sticky" top="0" zIndex={ZIndex.STICKY_HEADER.toString()}>
          <PtCondensedBanner
            priceTransparencyLevel={priceTransparencyLevel}
            onClick={openFeeExplanationModal}
            bookingFeePercentage={bookingFeePercentage}
            isSitterProfile={false}
          />
        </Box>
      )}

      {selectedProviderResult && selectedProviderHeader && (
        <>
          <SearchSectionHeader
            id={HeaderScenariosEnum.SELECTED_SITTER}
            headerIconName={selectedProviderHeader.icon}
            headerText={selectedProviderHeader.title}
            primarySubheaderText={selectedProviderHeader.primarySubheader}
            secondarySubheaderText={selectedProviderHeader.secondarySubheader}
            alertText={selectedProviderHeader.alertText}
            alertSeverity={selectedProviderHeader.alertSeverity}
            alertLinkHref={getBookingFeeLearnMoreUrl(countryConfigUrls)}
            alertLinkEvent={alertLinkEvent}
          />
          <SelectedProvider
            startDate={searchQueryDates.startDate}
            endDate={searchQueryDates.endDate}
            dates={searchQueryDistinctDates.dates}
            displayExpanded={!isMapExpanded}
            isAuthenticated={isAuthenticated}
            locale={locale}
            memberProfileLinkParams={enhancedMemberProfileLinkParams}
            onFavoriteToggled={onFavoriteToggled}
            searchResult={selectedProviderResult}
            // @ts-expect-error
            ref={searchCardRefs}
            isSeoPage={isSeoPage}
            preferCatReviews={preferCatReviews}
            priceTransparencyLevel={priceTransparencyLevel}
          />
        </>
      )}

      {mainResultsHeader && (
        <Box display={[shouldHideBanner ? 'none' : 'block', 'block']}>
          <SearchSectionHeader
            id={HeaderScenariosEnum.SEARCH_RESULTS}
            headerIconName={mainResultsHeader.icon}
            headerText={mainResultsHeader.title}
            primarySubheaderText={mainResultsHeader.primarySubheader}
            secondarySubheaderText={mainResultsHeader.secondarySubheader}
            alertText={mainResultsHeader.alertText}
            alertSeverity={mainResultsHeader.alertSeverity}
            alertLinkHref={getBookingFeeLearnMoreUrl(countryConfigUrls)}
            alertLinkEvent={alertLinkEvent}
          />
        </Box>
      )}

      {noSearchResults ? emptyResultsComponent : mainSearchResults()}

      {alternateSearchResults.length > 0 && (
        <AlternateSearchResults
          displayExpanded={!isMapExpanded}
          isAuthenticated={isAuthenticated}
          locale={locale}
          memberProfileLinkParams={memberProfileLinkParams}
          onFavoriteToggled={onFavoriteToggled}
          searchResults={alternateSearchResults}
          serviceName={alternateServiceName}
          serviceDescription={alternateServiceDescription}
          isSeoPage={isSeoPage}
          preferCatReviews={preferCatReviews}
          priceTransparencyLevel={priceTransparencyLevel}
          showFromText={!searchQueryDates.startDate || !searchQueryDates.endDate}
          isShowingPriceInCurrency={isShowingPriceInCurrency}
          cardTreatment={cardTreatment}
        />
      )}

      {source === 'mobile_web'
        ? shouldDiplayPtMessage && (
            <FeeExplanationBottomSheet
              isOpen={isFeeExplanationModalOpen}
              onClose={() => setIsFeeExplanationModalOpen(false)}
              priceTransparencyLevel={priceTransparencyLevel}
              bookingPercentageFee={bookingFeePercentage}
            />
          )
        : shouldDiplayPtMessage && (
            <FeeExplanationModal
              isOpen={isFeeExplanationModalOpen}
              onClose={() => setIsFeeExplanationModalOpen(false)}
              priceTransparencyLevel={priceTransparencyLevel}
              bookingPercentageFee={bookingFeePercentage}
            />
          )}
    </>
  );
};

export default SearchResults;
